// src/oura/ui/foldState.ts
//
// Where a panel remembers that it was folded.
//
// The key is an explicit id, never the title (D14): "Named over time on
// ChatGPT" changes with the engine filter, and a fold keyed on its title
// forgot itself every time someone switched engines. The scope names the role
// and the screen, so the same panel id on two screens folds independently.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/** `<role>-<screen>`, e.g. "staff-overview" or "merchant-home". */
export const FoldScopeContext = createContext<string | null>(null);

/** `oura.fold.<scope>.<id>`, or null outside a FoldScope (not persisted). */
export function foldKey(scope: string | null, id: string): string | null {
    return scope ? `oura.fold.${scope}.${id}` : null;
}

/**
 * The stored state, or `fallback` when nothing is stored yet: a panel nobody
 * has touched takes its screen's default, and once someone folds or opens it,
 * it stays the way they left it.
 */
export function readFolded(key: string | null, fallback = false): boolean {
    if (!key) return fallback;
    try {
        const stored = window.localStorage.getItem(key);
        return stored === null ? fallback : stored === "1";
    } catch {
        // Private windows and blocked site data both throw here. The panel
        // takes its default, which is the useful state anyway.
        return fallback;
    }
}

function writeFolded(key: string | null, folded: boolean): void {
    if (!key) return;
    try {
        window.localStorage.setItem(key, folded ? "1" : "0");
    } catch {
        /* not worth failing a click over */
    }
}

/**
 * Cards a link unfolded for this visit only (`open(id, { persist: false })`)
 * before they mounted, by key. The card mounts open and the entry goes.
 */
const openThisVisit = new Set<string>();

/**
 * True while a link unfolds a card for this visit only: the card's own
 * handler sets its state as usual, and nothing it sets is stored.
 */
let unsaved = false;

/**
 * Folded state for one panel, read synchronously on mount so a folded panel
 * never flashes open first. Outside a FoldScope the state lives in memory
 * only: persisting under a guessed scope would collide across screens.
 */
export function useFoldState(key: string | null, defaultFolded = false): [boolean, (folded: boolean) => void] {
    const [folded, setFolded] = useState(() => (key !== null && openThisVisit.has(key) ? false : readFolded(key, defaultFolded)));
    // Dropped after mount, not in the initializer: a strict-mode double
    // render reads it twice.
    useEffect(() => {
        if (key) openThisVisit.delete(key);
    }, [key]);
    const set = useCallback(
        (next: boolean) => {
            setFolded(next);
            if (!unsaved) writeFolded(key, next);
        },
        [key],
    );
    return [folded, set];
}

/* ------------------------------------------------------------------------- *
 * Opening a card from elsewhere on the page
 * ------------------------------------------------------------------------- */

/**
 * The cards on screen, by their stored key. Module-level rather than held by
 * FoldScope, because the component that renders a FoldScope is usually the
 * one holding the link that opens a card in it, and it sits above its own
 * provider. The key already carries the scope, so the registry is per scope.
 */
const mounted = new Map<string, Set<() => void>>();

/** Register a mounted card so `useFoldOpener` can reach it. */
export function useFoldReveal(key: string | null, reveal: () => void): void {
    // The latest handler, read at call time: it closes over the card's
    // current folded state.
    const latest = useRef(reveal);
    useEffect(() => {
        latest.current = reveal;
    }, [reveal]);

    useEffect(() => {
        if (!key) return;
        const run = () => latest.current();
        const set = mounted.get(key) ?? new Set<() => void>();
        mounted.set(key, set);
        set.add(run);
        return () => {
            set.delete(run);
            if (set.size === 0) mounted.delete(key);
        };
    }, [key]);
}

/**
 * `open(id)` unfolds the card with that id in place, scrolls it into view and
 * moves focus to its head: the card keeps its own state (a chosen range, a
 * running refresh), because nothing remounts. A card that is not mounted
 * right now opens when it next mounts.
 *
 * Pass `scope` when calling from the component that renders the FoldScope,
 * which sits above its own provider; below one, the scope is read from it.
 *
 * `{ persist: false }` unfolds it for this visit only and stores nothing, so
 * the next visit takes the stored or default state again: for a deep link or
 * a row moving into a group, which are not a hand fold (YW, 29 Sep 2026).
 */
export function useFoldOpener(scope?: string): (id: string, options?: { persist?: boolean }) => void {
    const provided = useContext(FoldScopeContext);
    const resolved = scope ?? provided;
    return useCallback(
        (id: string, options?: { persist?: boolean }) => {
            const key = foldKey(resolved, id);
            if (!key) return;
            const reveals = mounted.get(key);
            if (options?.persist !== false) {
                writeFolded(key, false);
                reveals?.forEach((reveal) => reveal());
                return;
            }
            if (!reveals?.size) {
                openThisVisit.add(key);
                return;
            }
            unsaved = true;
            try {
                reveals.forEach((reveal) => reveal());
            } finally {
                unsaved = false;
            }
        },
        [resolved],
    );
}
